import { useEffect, useState, type ComponentType, type FormEvent, type SVGProps } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { Category, Channel, ChannelType, CommunityStructure } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { cn } from "../../lib/cn";
import { Dialog, confirm } from "../../components/ui/Dialog";
import { Button } from "../../components/ui/Button";
import { Field, Input } from "../../components/ui/Input";
import { SettingRow, Switch } from "../../components/ui/Controls";
import { toast } from "../../components/ui/Toast";
import { IconAnnouncement, IconHash, IconVolume } from "../../components/icons";
import { IconForum, IconMediaChannel } from "../../components/icons";
import { IconStage } from "../../components/icons";
import { offerPermissionSync } from "./permissionSync";

const CHANNEL_TYPES: { value: ChannelType; label: string; description: string; icon: ComponentType<SVGProps<SVGSVGElement>> }[] = [
  { value: "text", label: "Text", description: "Messages, images, GIFs, polls and more", icon: IconHash },
  { value: "announcement", label: "Announcement", description: "Important updates; only some roles can post", icon: IconAnnouncement },
  { value: "voice", label: "Voice", description: "Hang out with voice and screen share", icon: IconVolume },
  { value: "stage", label: "Stage", description: "Events with speakers and a listening audience", icon: IconStage },
  { value: "forum", label: "Forum", description: "Organised discussions, one post per topic", icon: IconForum },
  { value: "media", label: "Media", description: "A gallery of image and video posts", icon: IconMediaChannel },
];

/** Discord-style list of channel types: icon, name and a line on what each is for. */
function ChannelTypePicker({ value, onChange }: { value: ChannelType; onChange: (v: ChannelType) => void }) {
  return (
    <fieldset>
      <legend className="mb-1.5 text-sm font-medium text-fg-2">Channel type</legend>
      <div role="radiogroup" aria-label="Channel type" className="space-y-1">
        {CHANNEL_TYPES.map((t) => {
          const on = value === t.value;
          return (
            <label
              key={t.value}
              className={cn(
                "flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 transition-colors",
                on ? "border-accent bg-accent-soft" : "border-line bg-surface hover:border-line-strong hover:bg-surface-2",
              )}
            >
              <t.icon className={cn("icon size-5 shrink-0", on ? "text-fg" : "text-fg-3")} aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-fg">{t.label}</span>
                <span className="block truncate text-xs text-fg-3">{t.description}</span>
              </span>
              <input type="radio" name="channel-type" value={t.value} checked={on} onChange={() => onChange(t.value)} className="size-4 shrink-0 accent-[var(--accent)]" />
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

/** Text-like channel names are lower-case-with-dashes; voice and stage names keep their spelling. */
const liveName = (v: string, type: ChannelType) => (type === "voice" || type === "stage" ? v : v.toLowerCase().replace(/\s/g, "-"));

const SLOW_MODES = [
  { v: 0, l: "Off" },
  { v: 5, l: "5s" },
  { v: 10, l: "10s" },
  { v: 30, l: "30s" },
  { v: 60, l: "1m" },
  { v: 300, l: "5m" },
  { v: 900, l: "15m" },
  { v: 3600, l: "1h" },
];

export function RoleChecklist({ roles, value, onChange }: { roles: CommunityStructure["roles"]; value: string[]; onChange: (v: string[]) => void }) {
  const list = roles.filter((r) => !r.isDefault);
  if (!list.length) return <p className="text-xs text-fg-3">Create roles in community settings to grant access. Owners and administrators always have access.</p>;
  return (
    <div className="max-h-48 space-y-0.5 overflow-y-auto rounded-md border border-line p-1">
      {list.map((r) => {
        const on = value.includes(r.id);
        return (
          <label key={r.id} className="flex h-9 cursor-pointer items-center gap-2.5 rounded px-2 text-sm hover:bg-surface">
            <input type="checkbox" checked={on} onChange={() => onChange(on ? value.filter((x) => x !== r.id) : [...value, r.id])} className="size-4 accent-[var(--accent)]" />
            <span className="size-2.5 rounded-full" style={{ background: r.color ?? "var(--fg-3)" }} aria-hidden />
            {r.name}
          </label>
        );
      })}
    </div>
  );
}

export function ChannelDialog({
  open,
  onOpenChange,
  structure,
  channel,
  defaultCategoryId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  structure: CommunityStructure;
  channel?: Channel;
  defaultCategoryId: string | null;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const editing = !!channel;
  const [type, setType] = useState<ChannelType>("text");
  const [name, setName] = useState("");
  const [topic, setTopic] = useState("");
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [isPrivate, setPrivate] = useState(false);
  const [roles, setRoles] = useState<string[]>([]);
  /** New channels copy their category's permissions unless the access settings are changed here. */
  const [accessTouched, setAccessTouched] = useState(false);
  const [slow, setSlow] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setType(channel?.type ?? "text");
    setName(channel?.name ?? "");
    setTopic(channel?.topic ?? "");
    setCategoryId(channel ? channel.categoryId : defaultCategoryId);
    setPrivate(channel?.isPrivate ?? false);
    setRoles(channel?.allowedRoleIds ?? []);
    setSlow(channel?.slowModeSeconds ?? 0);
    setAccessTouched(false);
    setError(null);
  }, [open, channel, defaultCategoryId]);

  // Creating: show the access the channel will inherit from the chosen category.
  const category = structure.categories.find((c) => c.id === categoryId);
  useEffect(() => {
    if (!open || editing || accessTouched) return;
    setPrivate(category?.isPrivate ?? false);
    setRoles(category?.allowedRoleIds ?? []);
  }, [open, editing, accessTouched, category]);
  const inherits = !editing && !!category && !accessTouched;

  const refresh = () => qc.invalidateQueries({ queryKey: ["structure", structure.community.id] });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setError("Give the channel a name");
    setPending(true);
    try {
      if (editing) {
        await api.patch(`/api/channels/${channel!.id}`, { name, topic, categoryId, isPrivate, allowedRoleIds: roles, slowModeSeconds: slow });
        await refresh();
        onOpenChange(false);
        if (categoryId !== channel!.categoryId) {
          const fresh = qc.getQueryData<CommunityStructure>(["structure", structure.community.id]);
          const moved = fresh?.channels.find((c) => c.id === channel!.id);
          if (fresh && moved) await offerPermissionSync(qc, fresh, moved, categoryId);
        }
        return;
      }
      const r = await api.post<{ id: string }>(`/api/communities/${structure.community.id}/channels`, {
        name,
        type,
        topic,
        categoryId,
        // Left out, the server copies the category's permissions (the channel starts synced).
        ...(inherits ? {} : { isPrivate, allowedRoleIds: roles }),
      });
      await refresh();
      if (type !== "voice" && type !== "stage") navigate(`/c/${structure.community.id}/${r.id}`);
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  };

  const remove = async () => {
    if (!channel) return;
    const ok = await confirm({ title: `Delete #${channel.name}?`, description: "All messages in this channel will be permanently deleted.", confirmLabel: "Delete channel", danger: true });
    if (!ok) return;
    try {
      await api.del(`/api/channels/${channel.id}`);
      await refresh();
      onOpenChange(false);
      navigate(`/c/${structure.community.id}`);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? "Edit channel" : "Create channel"}
      footer={
        <>
          {editing && (
            <Button variant="ghost" className="mr-auto text-danger hover:bg-danger-soft hover:text-danger" onClick={() => void remove()}>
              Delete
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="channel-form" loading={pending}>
            {editing ? "Save" : "Create"}
          </Button>
        </>
      }
    >
      <form id="channel-form" onSubmit={submit} className="space-y-4" noValidate>
        {!editing && (
          <ChannelTypePicker
            value={type}
            onChange={(t) => {
              setType(t);
              setName((n) => liveName(n, t));
            }}
          />
        )}
        <Field label="Name" error={error}>
          {(p) => {
            const TypeIcon = CHANNEL_TYPES.find((t) => t.value === type)?.icon ?? IconHash;
            return (
              <div className="relative">
                <TypeIcon className="icon pointer-events-none absolute left-3 top-1/2 size-[18px] -translate-y-1/2 text-fg-3" aria-hidden />
                <Input
                  {...p}
                  value={name}
                  maxLength={32}
                  className="pl-9"
                  onChange={(e) => setName(liveName(e.target.value, type))}
                  placeholder={type === "voice" ? "Lounge" : type === "stage" ? "Town Hall" : "new-channel"}
                />
              </div>
            );
          }}
        </Field>
        {type !== "voice" && (
          <Field label="Topic">
            {(p) => <Input {...p} value={topic} maxLength={200} onChange={(e) => setTopic(e.target.value)} />}
          </Field>
        )}
        <Field label="Category">
          {(p) => (
            <select
              {...p}
              value={categoryId ?? ""}
              onChange={(e) => setCategoryId(e.target.value || null)}
              className="h-10 w-full rounded-md border border-line-strong bg-surface px-3 text-base text-fg focus:border-accent focus:outline-none"
            >
              <option value="">No category</option>
              {structure.categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
        </Field>
        {editing && type !== "voice" && (
          <div className="space-y-1.5">
            <div className="text-sm font-medium text-fg-2">Slow mode</div>
            <div className="flex flex-wrap gap-1">
              {SLOW_MODES.map((s) => (
                <button
                  type="button"
                  key={s.v}
                  onClick={() => setSlow(s.v)}
                  aria-pressed={slow === s.v}
                  className={cn("h-8 rounded-md px-2.5 text-sm tabular", slow === s.v ? "bg-surface-3 text-fg" : "text-fg-3 hover:bg-surface hover:text-fg")}
                >
                  {s.l}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="border-t border-line pt-1">
          <SettingRow title="Private channel" description="Only selected roles, administrators and the owner can see it." htmlFor="ch-private">
            <Switch
              id="ch-private"
              label="Private channel"
              checked={isPrivate}
              onCheckedChange={(v) => {
                setAccessTouched(true);
                setPrivate(v);
              }}
            />
          </SettingRow>
          {isPrivate && (
            <RoleChecklist
              roles={structure.roles}
              value={roles}
              onChange={(v) => {
                setAccessTouched(true);
                setRoles(v);
              }}
            />
          )}
          {inherits && <p className="mt-2 text-xs text-fg-3">Permissions will be synced with {category!.name}. Changing access here gives the channel its own.</p>}
        </div>
      </form>
    </Dialog>
  );
}

export function CategoryDialog({ open, onOpenChange, communityId, category }: { open: boolean; onOpenChange: (o: boolean) => void; communityId: string; category?: Category }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setName(category?.name ?? "");
      setError(null);
    }
  }, [open, category]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setError("Give the category a name");
    setPending(true);
    try {
      if (category) await api.patch(`/api/categories/${category.id}`, { name });
      else await api.post(`/api/communities/${communityId}/categories`, { name });
      await qc.invalidateQueries({ queryKey: ["structure", communityId] });
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  };
  const remove = async () => {
    if (!category) return;
    const ok = await confirm({ title: `Delete ${category.name}?`, description: "Channels in it will move to the top of the list.", confirmLabel: "Delete category", danger: true });
    if (!ok) return;
    try {
      await api.del(`/api/categories/${category.id}`);
      await qc.invalidateQueries({ queryKey: ["structure", communityId] });
      onOpenChange(false);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={category ? "Edit category" : "Create category"}
      size="sm"
      footer={
        <>
          {category && (
            <Button variant="ghost" className="mr-auto text-danger hover:bg-danger-soft hover:text-danger" onClick={() => void remove()}>
              Delete
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="category-form" loading={pending}>
            {category ? "Save" : "Create"}
          </Button>
        </>
      }
    >
      <form id="category-form" onSubmit={submit} noValidate>
        <Field label="Name" error={error}>
          {(p) => <Input {...p} value={name} maxLength={32} onChange={(e) => setName(e.target.value)} />}
        </Field>
      </form>
    </Dialog>
  );
}
